import { useDeepCompareCallback } from 'use-deep-compare'
import moment from 'moment'
import {
  FilterHookArgumentsBase,
  FilterInternalArgumentsBase,
} from '@core/providers/filters'
import { localizedStrings } from '@core/strings'
import _isEmpty from 'lodash/isEmpty'

export type AdvancedTimeFilterOperators =
  | 'IS_BEFORE'
  | 'IS_AFTER'
  | 'IS_BETWEEN'
  | 'IS_PRESENT'
  | 'IS_NOT_PRESENT'

export const advancedTimeFilterOperatorsWithoutArguments: AdvancedTimeFilterOperators[] =
  ['IS_PRESENT', 'IS_NOT_PRESENT']

export type AdvancedTimeFilterValue = {
  operator: AdvancedTimeFilterOperators
  value: {
    timeValue?: string
    startTime?: string
    endTime?: string
  }
}

export const advancedTimeFilterValueIsPartial = (
  value: AdvancedTimeFilterValue,
) => {
  if (!value?.operator) {
    return true
  }

  if (advancedTimeFilterOperatorsWithoutArguments.includes(value?.operator)) {
    return false
  }

  if (_isEmpty(value?.value)) {
    return true
  }

  if (
    !value?.value?.timeValue &&
    (!value?.value?.startTime || !value?.value?.endTime)
  ) {
    return true
  }

  return false
}

export const advancedTimeFilterOptionConditions: {
  value: AdvancedTimeFilterOperators
  label: string
}[] = [
  { value: 'IS_BEFORE', label: 'Is before' },
  { value: 'IS_AFTER', label: 'Is after' },
  { value: 'IS_BETWEEN', label: 'Is between' },
  { value: 'IS_PRESENT', label: localizedStrings.isPresent },
  { value: 'IS_NOT_PRESENT', label: localizedStrings.isNotPresent },
]

export type AdvancedTimeFilterHookArguments =
  FilterHookArgumentsBase<AdvancedTimeFilterValue> & {
    // Any extra arguments here
    UTC?: boolean
  }

export type AdvancedTimeFilterInternalArguments =
  AdvancedTimeFilterHookArguments &
    FilterInternalArgumentsBase<AdvancedTimeFilterValue, 'ADVANCED_TIME'>

export const useAdvancedTimeFilter: (
  filter: AdvancedTimeFilterHookArguments,
) => AdvancedTimeFilterInternalArguments = (filter) => {
  const nothingSelectedText =
    filter.nothingSelectedText || localizedStrings.noTimeSelected

  const unfocusedText = useDeepCompareCallback(
    (
      filterValue: AdvancedTimeFilterValue,
      { noLabel } = { noLabel: false },
    ) => {
      const value = filterValue?.value
      const label = noLabel ? '' : `${filter.label}: `

      const operatorLabel = advancedTimeFilterOptionConditions.find(
        (operator) => operator.value === filterValue?.operator,
      )?.label

      if (
        filterValue?.operator === 'IS_PRESENT' ||
        filterValue?.operator === 'IS_NOT_PRESENT'
      ) {
        return `${label}${operatorLabel}`
      }

      const timeString = (time) =>
        moment
          .utc(`${moment().format('YYYY-MM-DD')} ${time}`, 'YYYY-MM-DD HH:mm')
          .format('HH:mm')

      if (!value?.timeValue && !value?.startTime && !value?.endTime) {
        return `${label}${nothingSelectedText}`
      }

      const startTimeString = value?.startTime
        ? timeString(value?.startTime)
        : ''
      const endTimeString = value?.endTime
        ? ` - ${timeString(value?.endTime)}`
        : ''

      const timeValueString = value?.timeValue
        ? timeString(value?.timeValue)
        : ''

      return value?.timeValue
        ? `${
            noLabel ? '' : filter.label
          } ( ${operatorLabel} ) ${timeValueString}`
        : `${
            noLabel ? '' : filter.label
          } ( ${operatorLabel} ) ${startTimeString}${endTimeString}`
    },
    [filter],
  )

  return {
    type: 'ADVANCED_TIME',
    unfocusedText,
    nothingSelectedText,
    serialization: 'object',
    ...filter,
  }
}
